import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallState {
  isInstalled: boolean;
  isInstallable: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  canShowNativePrompt: boolean;
}

interface UsePWAInstallReturn extends PWAInstallState {
  triggerInstall: () => Promise<'installed' | 'dismissed' | 'show-guide'>;
}

export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState<PWAInstallState>({
    isInstalled: false,
    isInstallable: false,
    isIOS: false,
    isAndroid: false,
    isDesktop: false,
    canShowNativePrompt: false,
  });

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/.test(userAgent) && !(window as any).MSStream;
    const isAndroid = /android/.test(userAgent);
    const isDesktop = !isIOS && !isAndroid;

    setState(prev => ({
      ...prev,
      isInstalled: isStandalone,
      isIOS,
      isAndroid,
      isDesktop,
      // iOS는 beforeinstallprompt가 없으므로 installable은 true로 설정
      isInstallable: isIOS ? !isStandalone : false,
    }));

    if (isStandalone) {
      return; // Already installed, no need to listen
    }

    // Listen for install prompt (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setState(prev => ({
        ...prev,
        isInstallable: true,
        canShowNativePrompt: true,
      }));
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setState(prev => ({
        ...prev,
        isInstalled: true,
        isInstallable: false,
        canShowNativePrompt: false,
      }));
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async (): Promise<'installed' | 'dismissed' | 'show-guide'> => {
    // For Android/Desktop with native prompt
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        
        if (outcome === 'accepted') {
          setState(prev => ({ ...prev, isInstalled: true, isInstallable: false }));
          return 'installed';
        }
        return 'dismissed';
      } catch (error) {
        console.error('[PWA] Install prompt error:', error);
        return 'dismissed';
      }
    }

    // For iOS or when native prompt is not available
    return 'show-guide';
  }, [deferredPrompt]);

  return {
    ...state,
    triggerInstall,
  };
}
